import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../app/utils/baseQueryWithReauth';

const latestSeq = {
    template: new Map(),
    project: new Map(),
};

function nextSeq(map, key) {
    const current = map.get(key) || 0;
    const next = current + 1;
    map.set(key, next);
    return next;
}

export const constructorApi = createApi({
    reducerPath: 'constructorApi',
    tagTypes: ['Project', 'Projects', 'Template', 'Templates', 'Form', 'Product'],
    baseQuery: baseQueryWithReauth(process.env.REACT_APP_CONSTRUCTOR_SERVICE_URL),
    endpoints: (builder) => ({
        getTemplates: builder.query({
            query: () => '/v1/templates',
            providesTags: (result) => [{ type: 'Templates', id: 'LIST' }]
        }),
        getTemplateById: builder.query({
            query: (templateId) => `/v1/templates/${templateId}`,
            providesTags: (result, error, id) => [{ type: 'Template', id }]
        }),
        createTemplate: builder.mutation({
            query: (data) => ({ url: '/v1/templates/new', method: 'POST', body: data }),
            invalidatesTags: [{ type: 'Templates', id: 'LIST' }]
        }),
        updateTemplate: builder.mutation({
            query: ({ templateId, data }) => ({ url: `/v1/templates/${templateId}`, method: 'PATCH', body: data }),
            async onQueryStarted({ templateId, data }, { dispatch, queryFulfilled }) {
                const seq = nextSeq(latestSeq.template, templateId);
                const patchDetail = dispatch(
                    constructorApi.util.updateQueryData('getTemplateById', templateId, (draft) => {
                        if (!draft) return;
                        if (data.base_config !== undefined) {
                            draft.base_config = { ...(draft.base_config || {}), ...data.base_config };
                        }
                        if (data.name !== undefined) draft.name = data.name;
                        if (data.type !== undefined) draft.type = data.type;
                        if (data.description !== undefined) draft.description = data.description;
                        if (data.preview_url !== undefined) draft.preview_url = data.preview_url;
                    })
                );
                const patchList = dispatch(
                    constructorApi.util.updateQueryData('getTemplates', undefined, (draft) => {
                        if (!Array.isArray(draft)) return;
                        const item = draft.find((t) => t.id === templateId);
                        if (!item) return;
                        if (data.name !== undefined) item.name = data.name;
                        if (data.type !== undefined) item.type = data.type;
                        if (data.description !== undefined) item.description = data.description;
                        if (data.preview_url !== undefined) item.preview_url = data.preview_url;
                    })
                );
                try {
                    const { data: updated } = await queryFulfilled;
                    if (latestSeq.template.get(templateId) !== seq) return; // stale response
                    dispatch(
                        constructorApi.util.updateQueryData('getTemplateById', templateId, (draft) => {
                            if (!draft) return;
                            Object.assign(draft, updated);
                        })
                    );
                    dispatch(
                        constructorApi.util.updateQueryData('getTemplates', undefined, (draft) => {
                            if (!Array.isArray(draft)) return;
                            const item = draft.find((t) => t.id === templateId);
                            if (item) Object.assign(item, updated);
                        })
                    );
                } catch {
                    if (latestSeq.template.get(templateId) === seq) {
                        patchDetail.undo();
                        patchList.undo();
                    }
                }
            },
        }),
        deleteTemplate: builder.mutation({
            query: (templateId) => ({ url: `/v1/templates/${templateId}`, method: 'DELETE' }),
            invalidatesTags: [{ type: 'Templates', id: 'LIST' }]
        }),
        validateBotToken: builder.mutation({
            query: ({ bot_token }) => ({
                url: '/v1/bots/validate',
                method: 'POST',
                body: { bot_token }
            }),
        }),
        updateProject: builder.mutation({
            query: ({ projectId, projectData }) => ({
                url: `/v1/projects/${projectId}`,
                method: 'PATCH',
                body: projectData
            }),
            async onQueryStarted({ projectId, projectData }, { dispatch, queryFulfilled }) {
                const seq = nextSeq(latestSeq.project, projectId);
                const patchProject = dispatch(
                    constructorApi.util.updateQueryData('getProjectById', projectId, (draft) => {
                        if (!draft) return;
                        Object.assign(draft, projectData);
                    })
                );
                const patchList = dispatch(
                    constructorApi.util.updateQueryData('getCurrentUserProjects', undefined, (draft) => {
                        if (!Array.isArray(draft)) return;
                        const item = draft.find((p) => p.id === projectId);
                        if (item) {
                            if (projectData.title !== undefined) item.title = projectData.title;
                            if (projectData.status !== undefined) item.status = projectData.status;
                            if (projectData.settings !== undefined) item.settings = projectData.settings;
                            if (projectData.structure !== undefined) item.structure = projectData.structure;
                        }
                    })
                );
                try {
                    const { data } = await queryFulfilled;
                    if (latestSeq.project.get(projectId) !== seq) return; // stale response
                    dispatch(
                        constructorApi.util.updateQueryData('getProjectById', projectId, (draft) => {
                            if (!draft) return;
                            Object.assign(draft, data);
                        })
                    );
                    dispatch(
                        constructorApi.util.updateQueryData('getCurrentUserProjects', undefined, (draft) => {
                            if (!Array.isArray(draft)) return;
                            const item = draft.find((p) => p.id === projectId);
                            if (item) Object.assign(item, data);
                        })
                    );
                } catch {
                    if (latestSeq.project.get(projectId) === seq) {
                        patchProject.undo();
                        patchList.undo();
                    }
                }
            },
        }),
        getProjectById: builder.query({
            query: (projectId) => `/v1/projects/${projectId}`,
            providesTags: (result, error, projectId) => [{ type: 'Project', id: projectId }]
        }),
        getProjectBot: builder.query({
            query: (projectId) => `/v1/projects/${projectId}/bot`,
            providesTags: (result, error, projectId) => [{ type: 'Project', id: projectId }]
        }),
        linkProjectBot: builder.mutation({
            query: ({ projectId, bot_token }) => ({
                url: `/v1/projects/${projectId}/bot`,
                method: 'POST',
                body: { bot_token }
            }),
            invalidatesTags: (result, error, { projectId }) => [
                { type: 'Project', id: projectId }
            ]
        }),
        getCurrentUserProjects: builder.query({
            query: () => '/v1/projects',
            providesTags: (result) => [{ type: 'Projects', id: 'LIST' }]
        }),
        createNewProject: builder.mutation({
            query: (projectData) => ({
                url: '/v1/projects/new',
                method: 'POST',
                body: projectData
            }),
            invalidatesTags: [{ type: 'Projects', id: 'LIST' }]
        }),
        deleteProject: builder.mutation({
            query: (projectId) => ({
                url: `v1/projects/${projectId}`,
                method: 'DELETE'
            }),
            invalidatesTags: [{ type: 'Projects', id: 'LIST' }]
        }),
        changeProjectStatus: builder.mutation({
            query: ({ projectId, status }) => ({
                url: `v1/projects/${projectId}/status`,
                method: 'PATCH',
                body: { status }
            }),
            invalidatesTags: (result, error, { projectId }) => [
                { type: 'Project', id: projectId },
                { type: 'Projects', id: 'LIST' }
            ]
        }),
        getFormById: builder.query({
            query: ({ projectId, formId }) => `/v1/projects/${projectId}/forms/${formId}/schema`,
            providesTags: (result, error, { formId }) => [{ type: 'Form', id: formId }]
        }),
        createFormInProject: builder.mutation({
            query: ({ projectId, formData }) => ({
                url: `/v1/projects/${projectId}/forms`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: (result) => result ? [{ type: 'Form', id: result }] : []
        }),
        updateForm: builder.mutation({
            query: ({ formId, projectId, formData }) => ({
                url: `/v1/projects/${projectId}/forms/${formId}`,
                method: 'PATCH',
                body: formData,
            }),
            invalidatesTags: (result, error, { formId }) => [
                { type: 'Form', id: formId }
            ]
        }),
        listFormVersions: builder.query({
            query: ({ projectId, formId }) => ({
                url: `/v1/projects/${projectId}/forms/${formId}/versions`,
                method: 'GET',
            }),
        }),
        getProjectProducts: builder.query({
            query: ({ projectId, only_active, limit = 100, offset = 0 }) => ({
                url: `/v1/projects/${projectId}/products`,
                method: 'GET',
                params: {
                    ...(only_active !== undefined ? { only_active } : {}),
                    limit,
                    offset,
                },
            }),
            providesTags: (result, error, { projectId }) => [
                { type: 'Product', id: 'LIST' }, // generic list for bulk invalidation
                { type: 'Product', id: `LIST:${projectId}` },
                ...(result?.items || []).map((p) => ({ type: 'Product', id: p.id }))
            ],
        }),
        createProduct: builder.mutation({
            query: ({ productData }) => ({
                url: '/v1/products',
                method: 'POST',
                body: productData,
            }),
            invalidatesTags: (result, error, { productData }) => [
                { type: 'Product', id: 'LIST' },
                { type: 'Product', id: `LIST:${productData?.project_id}` },
            ],
        }),
        updateProduct: builder.mutation({
            query: ({ productId, productData }) => ({
                url: `/v1/products/${productId}`,
                method: 'PATCH',
                body: productData,
            }),
            invalidatesTags: (result, error, { productId, productData }) => {
                const projectId = productData?.project_id || result?.project_id;
                return [
                    { type: 'Product', id: 'LIST' },
                    ...(projectId ? [{ type: 'Product', id: `LIST:${projectId}` }] : []),
                    { type: 'Product', id: productId },
                ];
            },
        }),
        deleteProduct: builder.mutation({
            query: ({ productId }) => ({
                url: `/v1/products/${productId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { projectId }) => (
                projectId
                    ? [
                        { type: 'Product', id: 'LIST' },
                        { type: 'Product', id: `LIST:${projectId}` }
                    ]
                    : [{ type: 'Product', id: 'LIST' }]
            ),
        }),
        bulkDeleteProducts: builder.mutation({
            query: ({ ids }) => ({
                url: '/v1/products/bulk-delete',
                method: 'POST',
                body: { ids },
            }),
            invalidatesTags: (result) => {
                if (!result) return [];
                return [{ type: 'Product', id: 'LIST' }];
            }
        }),
    }),
});


export const {
    useGetTemplatesQuery,
    useGetTemplateByIdQuery,
    useCreateTemplateMutation,
    useUpdateTemplateMutation,
    useDeleteTemplateMutation,
    useValidateBotTokenMutation,
    useGetCurrentUserProjectsQuery,
    useCreateNewProjectMutation,
    useDeleteProjectMutation,
    useChangeProjectStatusMutation,
    useGetProjectByIdQuery,
    useUpdateProjectMutation,
    useGetProjectBotQuery,
    useLinkProjectBotMutation,
    useGetFormByIdQuery,
    useLazyGetFormByIdQuery,
    useCreateFormInProjectMutation,
    useUpdateFormMutation,
    useListFormVersionsQuery,
    useGetProjectProductsQuery,
    useCreateProductMutation,
    useUpdateProductMutation,
    useDeleteProductMutation,
    useBulkDeleteProductsMutation,
} = constructorApi;
